import React from 'react';
import DOMPurify from 'dompurify';
import { Calendar, User } from 'lucide-react';
import type { RmContentArticle } from '../../types/rmContent';
import RmContentFooter from './RmContentFooter';

interface RmArticleViewerProps {
  content: RmContentArticle;
}

const formatDate = (dateStr: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateStr));
  } catch {
    return '';
  }
};

const RmArticleViewer: React.FC<RmArticleViewerProps> = ({ content }) => {
  const teacherNames = content.teacher
    ?.map((t: any) => (typeof t === 'string' ? t : t.title || t.slug || ''))
    .filter(Boolean);

  return (
    <div>
      {/* Hero section */}
      {content.image?.src ? (
        <div className="relative w-full max-h-[400px] overflow-hidden">
          <img
            src={content.image.src}
            alt={content.title || ''}
            className="w-full h-full object-cover"
            style={{ maxHeight: 400 }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight drop-shadow-lg">
              {content.title}
            </h1>
          </div>
        </div>
      ) : (
        <div className="bg-rm-primary-700 px-6 sm:px-8 py-8 sm:py-10">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
              {content.title}
            </h1>
            <div className="mt-3 w-16 h-1 rounded-full bg-rm-accent-500" />
          </div>
        </div>
      )}

      {/* Meta bar */}
      {(content.publishedAt || teacherNames?.length > 0) && (
        <div className="max-w-3xl mx-auto px-6 pt-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            {content.publishedAt && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(content.publishedAt)}
              </span>
            )}
            {teacherNames?.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                {teacherNames.join(', ')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content column */}
      <div className="max-w-3xl mx-auto px-6 py-6">
        {content.intro && (
          <div
            className="prose prose-lg max-w-none text-gray-700 mb-6"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.intro) }}
          />
        )}

        {content.firstContent && (
          <div
            className="prose prose-base max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.firstContent) }}
          />
        )}

        {content.firstContent && content.secondContent && (
          <div className="flex justify-center py-6">
            <div className="w-12 h-px bg-rm-accent-500" />
          </div>
        )}

        {content.secondContent && (
          <div
            className="prose prose-base max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.secondContent) }}
          />
        )}

        <div className="px-0">
          <RmContentFooter pillar={content.pillar} teacher={content.teacher} tags={content.tags} />
        </div>
      </div>
    </div>
  );
};

export default RmArticleViewer;
